// Canned notes for the "Send to spec queue" drawer (migration 20261102130000).
// Managed in Agents → Note templates; the drawer's dropdown copies a body into
// the note textarea, so a job keeps whatever text it was actually sent with.
//
// A template can also require things of the job it goes out with
// (20261104170000): the requirement follows the template picked in the
// dropdown, not the note's wording, so editing the loaded text keeps it and
// switching back to "No note" drops it. The drawer checks before it queues;
// the ai_jobs trigger checks again for every other way a job gets in.

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseClient = SupabaseClient<any, 'public', any>;
const db = supabase as unknown as LooseClient;

/** spec_note_templates.requirements. One key per rule; a missing key is off. */
export type NoteRequirements = {
  attachment?: boolean;
};

/** Every rule a template can carry, in the order the editor lists them. A new rule is a row here plus its check in unmetRequirements. */
export const NOTE_REQUIREMENTS: ReadonlyArray<{ key: keyof NoteRequirements; label: string; hint: string }> = [
  { key: 'attachment', label: 'Requires attachment', hint: 'The drawer won’t queue this note until at least one file is attached.' },
];

export type NoteTemplate = {
  id: string;
  name: string;
  body: string;
  requirements: NoteRequirements;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

/** What the job is still missing for this template, as short phrases for the alert. Empty = good to queue. */
export function unmetRequirements(template: Pick<NoteTemplate, 'requirements'> | null, job: { attachments: number }): string[] {
  if (!template) return [];
  const missing: string[] = [];
  if (template.requirements.attachment && job.attachments === 0) missing.push('At least one attachment');
  return missing;
}

const asRequirements = (v: unknown): NoteRequirements =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as NoteRequirements) : {};

const toTemplate = (r: NoteTemplate): NoteTemplate => ({ ...r, requirements: asRequirements(r.requirements) });

export async function fetchNoteTemplates(): Promise<NoteTemplate[]> {
  const { data, error } = await db.from('spec_note_templates').select('*').order('name');
  if (error) throw error;
  return ((data || []) as NoteTemplate[]).map(toTemplate);
}

export async function saveNoteTemplate(
  id: string | null,
  input: { name: string; body: string; requirements: NoteRequirements },
  createdBy: string | null
): Promise<NoteTemplate> {
  const name = input.name.trim();
  if (!name) throw new Error('Give the template a name');
  const fields = { name, body: input.body, requirements: input.requirements };
  const query = id
    ? db.from('spec_note_templates').update({ ...fields, updated_at: new Date().toISOString() }).eq('id', id)
    : db.from('spec_note_templates').insert({ ...fields, created_by: createdBy });
  const { data, error } = await query.select('*').single();
  if (error) throw error;
  return toTemplate(data as NoteTemplate);
}

export async function deleteNoteTemplate(id: string): Promise<void> {
  const { error } = await db.from('spec_note_templates').delete().eq('id', id);
  if (error) throw error;
}
